import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { useState, type ReactElement } from 'react';

import type { LandingType } from '../../../hooks/usePlusWebsites';
import { UrlEditModal } from '../../../components/UrlEditModal';
import { pagePreviewPath } from '../../../paths';
import { validateExternalHomepageUrl } from '../../../validation';
import { useSwitchLandingType } from '../hooks/useSwitchLandingType';

export interface PageLandingSwitch {
  landingType: LandingType;
  externalHomepageUrl: string | null | undefined;
}

interface PageActionsMenuProps {
  pageName: string;
  pageId: string;
  websiteId: string;
  organisationId: string;
  onSeoSettings: () => void;
  /** Only the home page can swap between the builder and an external landing page. */
  landingSwitch?: PageLandingSwitch | null;
}

// Overflow actions for a page that exists. Pages that have not been created yet get an "Add Page"
// row instead, so there is nothing to preview or configure.
export function PageActionsMenu({
  pageName,
  pageId,
  websiteId,
  organisationId,
  onSeoSettings,
  landingSwitch,
}: PageActionsMenuProps): ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Called unconditionally to keep the hook order stable; only the home page renders its item.
  const landing = useSwitchLandingType({
    websiteId,
    landingType: landingSwitch?.landingType ?? 'in-app',
    externalHomepageUrl: landingSwitch?.externalHomepageUrl,
  });

  const closeMenu = () => setAnchorEl(null);

  // Each action closes the menu first, otherwise it stays open behind the new tab or modal.
  const runAndClose = (action: () => void) => () => {
    closeMenu();
    action();
  };

  return (
    <>
      <IconButton
        aria-label={`Actions for ${pageName}`}
        aria-haspopup="menu"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        className="vtwo:text-typography-primary!"
      >
        <MoreVertIcon />
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem
          onClick={runAndClose(() =>
            window.open(
              pagePreviewPath({ organisationId, websiteId, pageId }),
              '_blank',
              'noopener,noreferrer'
            )
          )}
        >
          Preview
        </MenuItem>
        <MenuItem onClick={runAndClose(onSeoSettings)}>SEO settings</MenuItem>
        {landingSwitch && (
          <MenuItem disabled={landing.isSwitching} onClick={runAndClose(landing.start)}>
            {landing.label}
          </MenuItem>
        )}
      </Menu>

      {/* Outside the menu: closing the menu would otherwise unmount the modal with it. */}
      {landingSwitch && (
        <UrlEditModal
          open={landing.isUrlModalOpen}
          title="External homepage URL"
          initialValue={landing.externalHomepageUrl}
          isSaving={landing.isSwitching}
          hint="Patients landing on your website will be sent to this URL"
          validate={validateExternalHomepageUrl}
          onClose={landing.closeUrlModal}
          onSave={landing.saveExternalUrl}
        />
      )}
    </>
  );
}
